import * as yup from 'yup';

import {
  DataType,
  FieldsListQuery_category_fields as Field,
} from '../../../types/api';

type T = (key: string, params: any) => string;

const typeMap = {
  [DataType.BOOLEAN]: yup.boolean(),
};
// eslint-disable-next-line no-control-regex,no-useless-escape,no-empty-character-class
const emailRegex =
  /^([!#-'*+/-9=?A-Z^-~-]+(\.[!#-'*+/-9=?A-Z^-~-]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
// eslint-disable-next-line no-control-regex,no-useless-escape,no-empty-character-class
const emptyEmailRegex =
  /^$|^([!#-'*+/-9=?A-Z^-~-]+(\.[!#-'*+/-9=?A-Z^-~-]+)*|"([]!#-[^-~ \t]|(\\[\t -~]))+")@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const requiredMap = {
  [DataType.ADDRESS]: (schema: yup.StringSchema, field: Field, t: T) => {
    const requiredError = t('translation:errors.required_error', {
      label: field.label,
    });

    return schema
      .matches(/.*"street":.*/, requiredError)
      .matches(/.*"house_number":.*/, requiredError)
      .matches(/.*"postal_code":.*/, requiredError)
      .matches(/.*"city":.*/, requiredError)
      .matches(/.*"country":.*/, requiredError)
      .matches(
        /.*"location":.*/,
        t('customer_form.address_modal.no_results', {}),
      )
      .required(t('translation:errors.required_error', { label: field.label }));
  },
  [DataType.BOOLEAN]: (schema: yup.Schema<boolean>, field: Field, t: T) => {
    return schema.oneOf(
      [true],
      field.label.length > 100
        ? t('translation:errors.required_error_no_label', {})
        : t('translation:errors.required_error', { label: field.label }),
    );
  },
};

const validationMap = {
  [DataType.ADDRESS]: null,
  [DataType.BOOLEAN]: null,
  [DataType.EMAIL]: (schema: yup.StringSchema, field: Field, t: T) => {
    return schema.matches(
      new RegExp(field.required ? emailRegex : emptyEmailRegex),
      t('translation:errors.invalid_error', { label: field.label }),
    );
  },
  [DataType.NUMBER]: null,
  [DataType.PHONE]: null,
  [DataType.TEXT]: null,
  [DataType.WEBSITE]: (schema: yup.StringSchema, field: Field, t: T) => {
    // TODO: should allow empty string too when not required
    return schema.url(
      t('translation:errors.invalid_error', { label: field.label }),
    );
  },
};

function generateFieldSchema(field: Field, t: T) {
  // If the field is not visible we don't want to validate it
  if (!field.visible) {
    return undefined;
  }

  // Set correct starting schema
  let fieldSchema = typeMap[field.dataType] || yup.string();
  // Check if the field is required
  if (field.required) {
    const requiredValidation = requiredMap[field.dataType];
    if (typeof requiredValidation === 'function') {
      fieldSchema = requiredValidation(fieldSchema, field, t);
    } else {
      fieldSchema = fieldSchema.required(
        field.label.length > 100
          ? t('translation:errors.required_error_no_label', {})
          : t('translation:errors.required_error', { label: field.label }),
      );
    }
  }

  // Add validation depending on the dataType
  const dataTypeValidation = validationMap[field.dataType];
  if (typeof dataTypeValidation === 'function') {
    fieldSchema = dataTypeValidation(fieldSchema, field, t);
  }

  // Add regex validation
  if (field.validators && field.validators.length > 0) {
    field.validators.forEach((validator) => {
      fieldSchema = fieldSchema.matches(
        new RegExp(field.required ? validator.regex : `^$|${validator.regex}`),
        validator.errorMessage ||
          t('translation:errors.invalid_error', { label: field.label }),
      );
    });
  }

  return fieldSchema;
}

export function createSchema(fields: Field[], t: T) {
  const schemaShape = (fields || []).reduce((prevValue, field) => {
    const fieldSchema = generateFieldSchema(field, t);

    if (fieldSchema !== undefined) {
      prevValue[field.id] = fieldSchema;
    }

    return prevValue;
  }, {});

  return yup.object().shape(schemaShape);
}
